var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { InMemoryWebStorage, UserManager, WebStorageStateStore, } from 'oidc-client-ts';
import { InMemoryLocalWebStorage } from './local-memory-storage';
import { OidcStateManager } from './state';
export * from './types/OIDCAuthConfiguration';
export class OidcService {
    get accessToken() {
        return this._accessToken;
    }
    get userInfo() {
        return this._userProfile;
    }
    constructor(configuration, storage) {
        this.prefix = 'oidc';
        this.setStorage(storage);
        this.setSettings(configuration);
        this.setupUrlRedirectHook();
        this.setupPageShowHook();
    }
    static create(configuration) {
        return __awaiter(this, void 0, void 0, function* () {
            const oidcService = new OidcService(configuration.oidcSettings, configuration.storage);
            yield oidcService.checkInitialAuth(configuration.shouldRedirectToOriginUrl);
            return oidcService;
        });
    }
    /**
     * redirects to the login page
     */
    login() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.userManager.clearStaleState();
            return this.userManager.signinRedirect({
                state: OidcStateManager.getInitialState(),
            });
        });
    }
    /**
     * redirects to the logout page
     */
    logout() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.userManager.clearStaleState();
            return this.userManager.signoutRedirect();
        });
    }
    /**
     * Revokes user token
     */
    revokeTokens() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.userManager.clearStaleState();
            return this.userManager.revokeTokens();
        });
    }
    /**
     * Opens hidden iframe with logout page
     * needs additional setup from keycloak side
     * and content security policy updates
     */
    silentLogout() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.userManager.clearStaleState();
            return this.userManager.signoutSilent();
        });
    }
    /**
     * Returns access token and refreshes it if needed
     * @param force force token refresh
     */
    getAccessToken(force = false) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.refreshToken(force);
            return this._accessToken;
        });
    }
    /**
     * saves config settings and initialise the UserManager
     * @param configuration configuration options for the UserManager
     */
    setSettings(configuration) {
        const settings = Object.assign({ userStore: new WebStorageStateStore({
                prefix: this.prefix,
                store: this.storage,
            }) }, configuration);
        this.userManager = new UserManager(settings);
        this.userManager.events.addUserLoaded(() => __awaiter(this, void 0, void 0, function* () {
            yield this.checkAndRetrieveAccessToken();
        }));
    }
    /**
     * sets storage options based on configuration,
     * @param storageOption type of storage to use, default is localstorage as before
     * @private
     */
    setStorage(storageOption) {
        switch (storageOption) {
            case 'localstorage':
                this.storage = localStorage;
                break;
            case 'memory':
                this.storage = new InMemoryWebStorage();
                break;
            case 'sessionstorage':
                this.storage = sessionStorage;
                break;
            case 'localmemory':
                this.storage = new InMemoryLocalWebStorage(this.prefix);
                break;
            default:
                this.storage = localStorage;
        }
    }
    /**
     * Adds event listener to re-check authentication when the user uses the browser history to change url
     */
    setupUrlRedirectHook() {
        window.addEventListener('popstate', (event) => {
            if (event.singleSpa) {
                this.authenticate();
            }
        });
    }
    /**
     * Adds pageshow listener to handle bfcache
     * when user clicks back button page _might_ return from bfcache see
     * https://github.com/fergald/explainer-bfcache-ccns/blob/main/README.md
     * we want to check if user still logged-in
     * @private
     */
    setupPageShowHook() {
        window.addEventListener('pageshow', (e) => {
            if (e.persisted) {
                this.authenticate();
            }
        });
        // TODO: consider adding authenticate for visibility change
        /**
         window.addEventListener('visibilitychange', (e) => {
         this.authenticate()
         });
         */
    }
    /**
     * TODO:
     * @param shouldRedirectToOriginUrl configuration to determine if you want to redirect back to the original url you started at
     */
    checkInitialAuth(shouldRedirectToOriginUrl) {
        return __awaiter(this, void 0, void 0, function* () {
            if (location.href.includes(this.userManager.settings.redirect_uri)) {
                yield this.signinCallback(shouldRedirectToOriginUrl);
            }
            return this.authenticate();
        });
    }
    /**
     * TODO:
     * @param shouldRedirectToOriginUrl configuration to determine if you want to redirect back to the original url you started at
     */
    signinCallback(shouldRedirectToOriginUrl) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const user = yield this.userManager.signinRedirectCallback();
                const state = OidcStateManager.fromState(user.state);
                // Avoid infinite loop
                if (shouldRedirectToOriginUrl &&
                    !state.initialUrl.includes(this.userManager.settings.redirect_uri)) {
                    window.location.href = state.initialUrl;
                }
            }
            catch (e) {
                return this.login();
            }
        });
    }
    /**
     * checks if the user is already authenticated, and if not redirects to login page
     */
    authenticate() {
        return __awaiter(this, void 0, void 0, function* () {
            const accessToken = yield this.checkAndRetrieveAccessToken();
            if (!accessToken) {
                return this.login();
            }
        });
    }
    /**
     * checks if the user is already authenticated and the token is valid
     * @returns access token if there is one
     */
    checkAndRetrieveAccessToken() {
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield this.userManager.getUser();
            if (this.isUserAuthenticated(user)) {
                this._accessToken = user.access_token;
                this._userProfile = user.profile;
                return user.access_token;
            }
            this._accessToken = undefined;
            this._userProfile = undefined;
            return;
        });
    }
    /**
     * typeguard to confirm the user is authenticated
     * @returns true if the user authentication hasn't expired
     */
    isUserAuthenticated(user) {
        return !!user && !user.expired;
    }
    refreshToken(force = false) {
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield this.userManager.getUser();
            if ((user === null || user === void 0 ? void 0 : user.expired) || force) {
                const updatedUser = yield this.userManager.signinSilent();
                this._accessToken = updatedUser.access_token;
                this._userProfile = updatedUser.profile;
            }
        });
    }
}
//# sourceMappingURL=index.js.map